/* @flow */

import React, { PureComponent, createRef } from 'react'
import type { Node } from 'react'

export type BasicPropsType = {
  onChange?: (string | number | boolean | any) => void,
  valueHandler?: any,
  label?: string,
  optional?: string,
  id?: string,
  name?: string,
  value?: any,
  errorMessage?: string | boolean,
  infoTop?: string,
  infoBottom?: string,
  info?: string,
  prefix?: string,
  suffix?: string,
  tooltip?: Object,
  tabIndex?: number,
  rootClassName?: string,
  rootType?: string,
  wrapperClassName?: string,
  labelClassName?: string,
  hasErrorClassName?: string,
  hasLabelClassName?: string,
  hasFocusClassName?: string,
  requiredClassName?: string,
  optionalClassName?: string,
  hasTooltipClassName?: string,
  hasPrefixClassName?: string,
  hasSuffixClassName?: string,
  infoClassName?: string,
  prefixClassName?: string,
  suffixClassName?: string,
  errorClassName?: string,
  infoBottomClassName?: string,
  alignRightClassName?: string,
  emptyClassName?: string,
  required?: boolean,
  align?: string,
  children?: Node
}

type RefType = { current: any }

export type BasicStateType = {
  focus: boolean
}

// $FlowFixMe
export const InputContext = React.createContext({})

class Basic<Props: BasicPropsType, State: BasicStateType> extends PureComponent<Props, State> {
  static contextType = InputContext

  // $FlowFixMe
  state: State = {
    focus: false
  }

  inputRef: RefType = createRef()

  static defaultProps = {
    rootClassName: 'atp-input',
    labelClassName: 'atp-input__name',
    hasErrorClassName: 'atp-input--has-error',
    hasLabelClassName: 'atp-input--has-label',
    requiredClassName: 'atp-input--required',
    optionalClassName: 'atp-input__optional',
    hasTooltipClassName: 'atp-input--has-tooltip',
    hasPrefixClassName: 'atp-input--has-prefix',
    hasSuffixClassName: 'atp-input--has-suffix',
    hasFocusClassName: 'atp-input--has-focus',
    infoClassName: 'atp-input__info',
    prefixClassName: 'atp-input__prefix',
    suffixClassName: 'atp-input__suffix',
    errorClassName: 'atp-input__error',
    infoBottomClassName: 'atp-input__info-bottom',
    alignRightClassName: 'atp-input--align-right',
    emptyClassName: 'atp-input--empty',
    tabIndex: 0
  }

  handleChange(value: any): void {
    const { onChange } = this.getProps()
    if (onChange) onChange(value)
  }

  get id(): string {
    const props = this.getProps()
    return props.id || props.name || ''
  }

  get value(): any {
    const { value, valueHandler } = this.getProps()
    const newValue = value == null ? '' : value
    return typeof valueHandler == 'function' ? valueHandler(newValue) : newValue
  }

  get rootClass(): string {
    const props = this.getProps()
    const { rootClassName, rootType } = props
    if (rootClassName) {
      if (rootType) return `${rootClassName} ${rootClassName}--${rootType}`
      return rootClassName
    }
    return ''
  }

  get classNames(): string {
    const {
      wrapperClassName,
      hasErrorClassName,
      hasFocusClassName,
      hasLabelClassName,
      requiredClassName,
      hasTooltipClassName,
      hasPrefixClassName,
      hasSuffixClassName,
      alignRightClassName,
      emptyClassName,
      required,
      label,
      prefix,
      suffix,
      tooltip,
      errorMessage,
      align
    } = this.getProps()
    const { focus } = this.state
    const result = Array.isArray(wrapperClassName) ? wrapperClassName : [wrapperClassName]
    result.unshift(this.rootClass)
    if (errorMessage === true || typeof errorMessage == 'string') result.push(hasErrorClassName)
    if (label) result.push(hasLabelClassName)
    if (required) result.push(requiredClassName)
    if (tooltip) result.push(hasTooltipClassName)
    if (prefix) result.push(hasPrefixClassName)
    if (suffix) result.push(hasSuffixClassName)
    if (align == 'right') result.push(alignRightClassName)
    if (this.empty) result.push(emptyClassName)
    if (focus) result.push(hasFocusClassName)

    return result.filter(Boolean).join(' ')
  }

  getValue = (event: SyntheticInputEvent<any>): void => {
    this.handleChange(event.target.value)
  }

  formField(): ?React$Element<*> {
    throw 'overwrite me'
  }

  get empty() {
    return !this.value
  }

  focus() {
    if (this.inputRef && this.inputRef.current) {
      this.inputRef.current.focus()
    }
  }

  getProps() {
    return {
      ...this.context,
      ...this.props
    }
  }

  render(): React$Element<*> | null {
    const {
      label,
      tooltip,
      infoTop,
      infoBottom,
      prefix,
      suffix,
      labelClassName,
      optionalClassName,
      infoClassName,
      prefixClassName,
      suffixClassName,
      errorClassName,
      infoBottomClassName,
      optional,
      errorMessage,
      children
    } = this.getProps()
    return (
      <div className={this.classNames}>
        {label && (
          <label className={labelClassName} htmlFor={this.id}>
            {label} {tooltip} {optional && <span className={optionalClassName}>{optional}</span>}
          </label>
        )}
        {infoTop && <p className={infoClassName}>{infoTop}</p>}
        {this.formField()}
        {prefix && <span className={prefixClassName}>{prefix}</span>}
        {suffix && <span className={suffixClassName}>{suffix}</span>}
        {typeof errorMessage == 'string' && <p className={errorClassName}>{errorMessage}</p>}
        {infoBottom && <p className={infoBottomClassName}>{infoBottom}</p>}
        {children}
      </div>
    )
  }
}

export default Basic
